import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { X, Zap, MessageSquare, Loader2 } from "lucide-react";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const QuickAnswerSchema = Yup.object().shape({
  shortcut: Yup.string().min(2, "Muy corto!").max(15, "Muy largo!").required("Requerido"),
  message: Yup.string().min(8, "Muy corto!").max(30000, "Muy largo!").required("Requerido"),
});

const QuickAnswersModal = ({ open, onClose, quickAnswerId, initialValues, onSave }) => {
  const isMounted = useRef(true);
  const initialState = { shortcut: "", message: "" };
  const [quickAnswer, setQuickAnswer] = useState(initialState);

  useEffect(() => () => { isMounted.current = false; }, []);

  useEffect(() => {
    const fetchQuickAnswer = async () => {
      if (initialValues) setQuickAnswer((prev) => ({ ...prev, ...initialValues }));
      if (!quickAnswerId) return;
      try {
        const { data } = await api.get(`/quickAnswers/${quickAnswerId}`);
        if (isMounted.current) setQuickAnswer(data);
      } catch (err) { toastError(err); }
    };
    fetchQuickAnswer();
  }, [quickAnswerId, open, initialValues]);

  const handleClose = () => { onClose(); setQuickAnswer(initialState); };

  const handleSave = async (values) => {
    try {
      if (quickAnswerId) { await api.put(`/quickAnswers/${quickAnswerId}`, values); handleClose(); }
      else { const { data } = await api.post("/quickAnswers", values); if (onSave) onSave(data); handleClose(); }
      toast.success(i18n.t("quickAnswersModal.success"));
    } catch (err) { toastError(err); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" />
            {quickAnswerId ? i18n.t("quickAnswersModal.title.edit") : i18n.t("quickAnswersModal.title.add")}
          </h2>
          <button onClick={handleClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <Formik
          initialValues={quickAnswer}
          enableReinitialize={true}
          validationSchema={QuickAnswerSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => { handleSave(values); actions.setSubmitting(false); }, 400);
          }}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form>
              <div className="px-5 py-4 space-y-4 bg-slate-50 dark:bg-slate-800/30">
                <div>
                  <p className="flex items-center gap-1.5 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    <Zap className="w-3 h-3 text-amber-500" /> Atajo de teclado
                  </p>
                  <div className="flex items-center">
                    <span className="flex items-center px-3 h-full py-2.5 bg-slate-100 dark:bg-slate-700 border border-r-0 border-slate-200 dark:border-slate-700 rounded-l-lg text-indigo-600 font-bold text-base">/</span>
                    <Field
                      name="shortcut"
                      autoFocus
                      placeholder="hola"
                      className={`flex-1 px-3 py-2.5 text-sm border rounded-r-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-mono focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                        touched.shortcut && errors.shortcut ? "border-red-400" : "border-slate-200 dark:border-slate-700"
                      }`}
                    />
                  </div>
                  {touched.shortcut && errors.shortcut
                    ? <p className="text-xs text-red-500 mt-1">{errors.shortcut}</p>
                    : <p className="text-xs text-slate-400 mt-1">Escribe el comando sin el prefijo /. Ejemplo: hola</p>
                  }
                </div>
                <div>
                  <p className="flex items-center gap-1.5 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    <MessageSquare className="w-3 h-3 text-indigo-600" /> Mensaje de respuesta
                  </p>
                  <Field
                    as="textarea"
                    name="message"
                    rows={6}
                    placeholder="Ejemplo: ¡Hola! Gracias por contactarnos. ¿En qué podemos ayudarte hoy?"
                    className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none ${
                      touched.message && errors.message ? "border-red-400" : "border-slate-200 dark:border-slate-700"
                    }`}
                  />
                  {touched.message && errors.message
                    ? <p className="text-xs text-red-500 mt-1">{errors.message}</p>
                    : <p className="text-xs text-slate-400 mt-1">Escribe el mensaje que se enviará automáticamente</p>
                  }
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
                <button type="button" onClick={handleClose} disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                  {i18n.t("quickAnswersModal.buttons.cancel")}
                </button>
                <button type="submit" disabled={isSubmitting} className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                  {isSubmitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {quickAnswerId ? i18n.t("quickAnswersModal.buttons.okEdit") : i18n.t("quickAnswersModal.buttons.okAdd")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default QuickAnswersModal;
